import { useMemo, useState } from 'react';
import { Box, FileIcon, Film, ImageOff, Lock, Music } from 'lucide-react';
import { getAsset, getCdnImageUrlFromAsset } from '@/client/utils/assets';
import { cn } from '@/lib/utils';

interface AssetImageProps {
  asset: Asset;
  alt?: string;
  className?: string;
}

const KIND_ICON: Record<string, typeof FileIcon> = { video: Film, audio: Music, '3d': Box, document: FileIcon };

/**
 * Admin Library rows carry `image_urls`, built by the API from what is actually
 * stored (Uploadcare is purged for deleted assets, refuses to transform some
 * files, and private objects are ACL'd), so walk those. Rows from elsewhere fall
 * back to the client-side guesses. When every URL fails, render a tile — never
 * another network placeholder that can fail the same way.
 */
const AssetImage = ({ asset, alt, className }: AssetImageProps) => {
  const candidates = useMemo(() => {
    const urls = asset.image_urls ?? [
      getAsset(asset),
      getCdnImageUrlFromAsset(asset),
      asset.public_url || undefined,
      asset.public_url_uc || undefined,
    ];
    return Array.from(new Set(urls.filter((u): u is string => Boolean(u))));
  }, [asset]);

  const [idx, setIdx] = useState(0);
  const isImage = asset.kind ? asset.kind === 'image' : (asset.mime_type?.startsWith('image/') ?? true);

  if (!isImage || idx >= candidates.length) {
    const Icon = asset.private === 1 ? Lock : isImage ? ImageOff : KIND_ICON[asset.kind ?? ''] ?? FileIcon;
    const label = asset.private === 1 ? 'Private' : isImage ? 'Image unavailable' : asset.mime_type || asset.kind;
    return (
      <div
        className={cn('flex flex-col items-center justify-center gap-1 bg-muted text-muted-foreground', className)}
        role="img"
        aria-label={label ?? 'No preview'}
      >
        <Icon className="h-8 w-8 opacity-60" />
        <span className="px-2 text-center text-[11px]">{label}</span>
      </div>
    );
  }

  return (
    <img
      src={candidates[idx]}
      alt={alt ?? asset.name ?? 'Asset'}
      loading="lazy"
      className={className}
      onError={() => setIdx((i) => i + 1)}
    />
  );
};

export default AssetImage;
